import * as React from "react";
import { useState } from "react";
import styled from "styled-components";
import { useFormikContext } from "formik";
import { defaultCompare as naturalSort } from "logic/DefaultCompare";
import { ButtonGroup, Button, ControlLabel, FormGroup, HelpBlock, Input, Alert } from "components/bootstrap";
import { TIME_BASED_SIZE_OPTIMIZING_ROTATION_STRATEGY } from "hooks/useIndices";
import { formatArchiveRetentionTime } from "archive/index-set/archiveRetention";
import { ARCHIVE_RETENTION_LABELS, globalRetentionHelp } from "archive/index-set/archiveRetentionLabels";
import useIndexSetArchiveSettings from "archive/index-set/useIndexSetArchiveSettings";
import AppConfig from "util/AppConfig";
import { LICENSE_SUBJECTS } from "license/constants";
import LicenseCheck from "license/LicenseCheck";
import { ARCHIVE_LICENSE_WARNING_TITLE, ARCHIVE_LICENSE_WARNING_TEXT } from "archive/constants";
const StyledAlert = styled(Alert)`
  overflow: auto;
`;
const ArchiveRetentionStrategyConfiguration = ({
  config,
  jsonSchema = void 0,
  updateConfig,
  useMaxNumberOfIndices,
  disabled = false,
  hiddenFields = [],
  immutableFields = [],
  ignoreFieldRestrictions = false
}) => {
  var _a, _b, _c;
  const [maxNumberOfIndices, setMaxNumberOfIndices] = useMaxNumberOfIndices();
  const [configuration, setConfiguration] = useState(config);
  const restrictionName = (field) => `retention_strategy.${field}`;
  const archiveRetentionHidden = !ignoreFieldRestrictions && ["archive_retention_override_enabled", "archive_retention_time"].some(
    (field) => hiddenFields.includes(restrictionName(field))
  );
  const archiveRetentionDisabled = (field) => disabled || immutableFields.includes(restrictionName(field)) && !ignoreFieldRestrictions;
  const { globalRetentionTime } = useIndexSetArchiveSettings({
    enabled: !AppConfig.isCloud() && !archiveRetentionHidden
  });
  const {
    values: { rotation_strategy_class: rotationStrategyClass }
  } = useFormikContext();
  const updateConfiguration = (update) => {
    setConfiguration(update);
    updateConfig(update);
  };
  const onMaxIndicesUpdate = (e) => {
    const { value } = e.target;
    setMaxNumberOfIndices(value);
    updateConfiguration({ ...configuration, max_number_of_indices: value });
  };
  const onRetentionOverrideUpdate = (e) => {
    var _a2, _b2;
    const enabled = e.target.checked;
    if (archiveRetentionDisabled("archive_retention_time")) {
      updateConfiguration({ ...configuration, archive_retention_override_enabled: enabled });
      return;
    }
    const seeded = (_b2 = (_a2 = configuration.archive_retention_time) != null ? _a2 : globalRetentionTime) != null ? _b2 : null;
    updateConfiguration({
      ...configuration,
      archive_retention_override_enabled: enabled,
      archive_retention_time: enabled ? seeded : null
    });
  };
  const onRetentionTimeUpdate = (e) => {
    const { value } = e.target;
    updateConfiguration({ ...configuration, archive_retention_time: value === "" ? null : Number(value) });
  };
  const setIndexAction = (type) => () => {
    setConfiguration({ ...configuration, index_action: type });
    updateConfig({ ...configuration, index_action: type });
  };
  const indexActionActive = (type) => {
    const { index_action } = configuration;
    return index_action === type;
  };
  const hasValidSchema = () => !!jsonSchema;
  const indexActions = () => {
    if (hasValidSchema()) {
      const newIndexActions = jsonSchema.properties.index_action.enum;
      newIndexActions.sort((a, b) => naturalSort(a.toLowerCase(), b.toLowerCase()));
      return newIndexActions;
    }
    return [];
  };
  const buttons = (_a = indexActions()) == null ? void 0 : _a.map((action) => {
    const isActive = indexActionActive(action);
    return /* @__PURE__ */ React.createElement(
      Button,
      {
        key: `button-${action}`,
        bsSize: "small",
        onClick: setIndexAction(action),
        active: isActive,
        bsStyle: isActive ? "info" : "default"
      },
      action
    );
  });
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(
    LicenseCheck,
    {
      text: ARCHIVE_LICENSE_WARNING_TEXT,
      title: ARCHIVE_LICENSE_WARNING_TITLE,
      bsStyle: "danger",
      licenseSubject: LICENSE_SUBJECTS.archive
    }
  ), /* @__PURE__ */ React.createElement(StyledAlert, null, "The archive index retention strategy creates an archive for an index before deleting or closing it."), rotationStrategyClass !== TIME_BASED_SIZE_OPTIMIZING_ROTATION_STRATEGY && /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "number",
      id: "max-number-of-indices",
      label: "Max number of indices",
      onChange: onMaxIndicesUpdate,
      value: maxNumberOfIndices,
      min: 1,
      help: /* @__PURE__ */ React.createElement("span", null, "Maximum number of indices to keep before ", /* @__PURE__ */ React.createElement("strong", null, "archiving"), " the oldest ones"),
      autoFocus: true,
      required: true
    }
  )), !AppConfig.isCloud() && !archiveRetentionHidden && /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "checkbox",
      id: ARCHIVE_RETENTION_LABELS.overrideId,
      label: ARCHIVE_RETENTION_LABELS.overrideLabel,
      onChange: onRetentionOverrideUpdate,
      checked: (_b = configuration.archive_retention_override_enabled) != null ? _b : false,
      disabled: archiveRetentionDisabled("archive_retention_override_enabled"),
      help: ARCHIVE_RETENTION_LABELS.overrideHelp
    }
  ), configuration.archive_retention_override_enabled ? /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "number",
      id: ARCHIVE_RETENTION_LABELS.retentionTimeId,
      label: ARCHIVE_RETENTION_LABELS.retentionTimeLabel,
      onChange: onRetentionTimeUpdate,
      value: (_c = configuration.archive_retention_time) != null ? _c : "",
      min: 0,
      disabled: archiveRetentionDisabled("archive_retention_time"),
      help: ARCHIVE_RETENTION_LABELS.retentionTimeHelp,
      required: true
    }
  ) : /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      id: ARCHIVE_RETENTION_LABELS.globalId,
      label: ARCHIVE_RETENTION_LABELS.retentionTimeLabel,
      value: formatArchiveRetentionTime(globalRetentionTime),
      placeholder: ARCHIVE_RETENTION_LABELS.globalPlaceholder,
      help: globalRetentionHelp(globalRetentionTime),
      onChange: () => {
      },
      disabled: true
    }
  )), hasValidSchema() && /* @__PURE__ */ React.createElement(FormGroup, { id: "action-after-archive" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Action after archiving index"), /* @__PURE__ */ React.createElement(ButtonGroup, null, buttons), /* @__PURE__ */ React.createElement(HelpBlock, null, "Please select which action should be performed once an index has been archived.")));
};
export default ArchiveRetentionStrategyConfiguration;
